<template>
  <DialogFull :data="dialogSettings" @close="onClose">
    <template v-slot:dialog.body>
      <ValidationObserver ref="addSelfScheduleSlots">
        <div class="container col-lg-8 col-sm-12">
          <p class="mt-2 mb-0">Availability</p>
          <RadioButton
            :selectedValue="
              interviewSchedulingSettings.allParticipantsAvailable
            "
            @select="
              val =>
                handleInterviewSchedulingSettingsChange(
                  'allParticipantsAvailable',
                  val
                )
            "
            :options="availabilityOptions"
            showGroupInRow
          />
          <ValidationProvider
            v-slot="{ errors }"
            name="minimumSchedulingNotice"
            rules="required"
          >
            <v-text-field
              name="minimumSchedulingNotice"
              :min="0"
              type="number"
              :error-messages="errors"
              class="mt-6"
              aria-label="Minimum scheduling notice (hours)"
              label="Minimum scheduling notice (hours)"
              :value="interviewSchedulingSettings.minimumSchedulingNotice"
              @input="
                val =>
                  handleInterviewSchedulingSettingsChange(
                    'minimumSchedulingNotice',
                    val
                  )
              "
              v-lazy-input:debounce="1000"
            />
          </ValidationProvider>
          <v-row>
            <div class="col-md-6 col-sm-12">
              <ValidationProvider
                v-slot="{ errors }"
                name="bufferBeforeSlot"
                rules="required"
              >
                <v-text-field
                  name="bufferBeforeSlot"
                  :min="0"
                  type="number"
                  :error-messages="errors"
                  aria-label="Buffer before event (minutes)"
                  label="Buffer before event (minutes)"
                  :value="interviewSchedulingSettings.bufferBeforeSlot"
                  @input="
                    val =>
                      handleInterviewSchedulingSettingsChange(
                        'bufferBeforeSlot',
                        val
                      )
                  "
                  v-lazy-input:debounce="1000"
                />
              </ValidationProvider>
            </div>
            <div class="col-md-6 col-sm-12">
              <ValidationProvider
                v-slot="{ errors }"
                name="bufferAfterSlot"
                rules="required"
              >
                <v-text-field
                  name="bufferAfterSlot"
                  :min="0"
                  type="number"
                  :error-messages="errors"
                  aria-label="Buffer after event (minutes)"
                  label="Buffer after event (minutes)"
                  :value="interviewSchedulingSettings.bufferAfterSlot"
                  @input="
                    val =>
                      handleInterviewSchedulingSettingsChange(
                        'bufferAfterSlot',
                        val
                      )
                  "
                  v-lazy-input:debounce="1000"
                />
              </ValidationProvider>
            </div>
          </v-row>
          <v-row>
            <div class="slots-time-range-wrapper col-12">
              <v-label>Show available slots from</v-label>
              <TimeRangePicker
                class="mt-2"
                :fromHour="startTime"
                :toHour="endTime"
                @updateRange="updateTimeRange"
                disableValidation
              />
            </div>
          </v-row>
          <div class="mt-5" v-if="size(interviewers)">
            <div v-if="isLoadingAvailableSlots">
              <v-skeleton-loader
                type="date-picker-days"
                v-for="i in 2"
                :key="i"
              />
            </div>
            <div v-show="!isLoadingAvailableSlots">
              <BasicCalendar
                :events="events"
                :weekdays="[1, 2, 3, 4, 5, 6, 0]"
                @onDayClick="onDayClick"
                @onDateChange="onDateChange"
              />
            </div>
            <div class="my-5">
              <h2 class="mb-0">Select Slot</h2>
              <div v-if="size(selectedDays)">
                <div v-for="(selectedDay, index) in selectedDays" :key="index">
                  <div>
                    <div class="mt-3 d-flex justify-space-between">
                      <div class="mt-2">
                        <span class="font-weight-bold">
                          {{ selectedDay | dayFormatLong }}
                        </span>
                        <span class="secondary-text">
                          {{ availableSlots[selectedDay] | slotsCount }}
                        </span>
                      </div>
                      <PrimaryButton
                        @click.native="() => selectAll(selectedDay)"
                        text
                      >
                        Select All
                      </PrimaryButton>
                    </div>
                    <Checkbox
                      v-if="availableSlots[selectedDay]"
                      mixed
                      isLarge
                      :selected-values="selectedInterviewSlots"
                      :options="availableSlots[selectedDay]"
                      @change="val => onSlotSelect(val)"
                      @unselect="unselectSlot"
                    />
                    <div class="mt-2" v-else>
                      No slots found
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                No slots found
              </div>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </template>
    <template v-slot:dialog.action>
      <PrimaryButton @click.native="saveSlots">
        Save Slots
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import { INTERVIEWS_NAMESPACE } from "@/store/modules/bookings/interviews";
import { FETCH_INTERVIEW_FORMAT_AVAILABLE_SLOTS } from "@/store/modules/bookings/interviews/actions";
import DialogFull from "@/components/common/DialogFull";
import { createNamespacedHelpers } from "vuex";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { getFullName } from "@/utils/users";
import pluralize from "pluralize";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import {
  DATE_FORMAT_WITH_SEPARATOR_REVERSED,
  LONG_DATE_FORMAT
} from "@/constants/common.js";
import {
  map,
  size,
  filter,
  includes,
  find,
  forEach,
  delay,
  isNumber
} from "lodash";
import RadioButton from "@/components/common/RadioButton";
import BasicCalendar from "@/components/common/BasicCalendar";
import { formatDate, getDateRange } from "@/utils/time";
import { getFormattedLocation } from "@/utils/locations";
import {
  getColorBySlotAvailability,
  changeCalendarDaySelected,
  removeEventsFromInactiveDayFields,
  getAvailableSlotParamsForGivenMonth
} from "@/views/bookings/BookingInterviews/modules/utils";
import { AVAILABILITY_OPTIONS } from "@/views/bookings/BookingInterviews/modules/constants";
import Checkbox from "@/components/common/Checkbox";
import { DATA_NAMESPACE } from "@/store/modules/data";
import {
  GET_IS_FETCHING_DATA,
  GET_LOCATIONS
} from "@/store/modules/data/getters";
import TimeRangePicker from "@/components/common/TimePicker/TimeRangePicker";
import {
  INITIAL_START_TIME_VALUE,
  INITIAL_END_TIME_VALUE
} from "@/constants/interviews";

const { mapActions: mapInterviewActions } = createNamespacedHelpers(
  INTERVIEWS_NAMESPACE
);

const { mapGetters: mapDataGetters } = createNamespacedHelpers(DATA_NAMESPACE);

const INTERVIEW_INITIAL_SCHEDULING_SETTINGS = {
  allParticipantsAvailable: 1,
  minimumSchedulingNotice: 0,
  bufferBeforeSlot: 0,
  bufferAfterSlot: 0
};

export default {
  name: "AddSelfScheduleSlots",
  components: {
    DialogFull,
    ValidationProvider,
    ValidationObserver,
    RadioButton,
    BasicCalendar,
    Checkbox,
    TimeRangePicker,
    PrimaryButton
  },
  created() {
    this.selectedInterviewSlots = this.externalSelectedSlots;
    this.loadAvailableSlots();
  },
  data() {
    return {
      availabilityOptions: AVAILABILITY_OPTIONS,
      events: [],
      isLoadingAvailableSlots: false,
      availableSlots: {},
      selectedDays: [],
      calendarDate: new Date().toISOString(),
      interviewSchedulingSettings: { ...INTERVIEW_INITIAL_SCHEDULING_SETTINGS },
      startTime: INITIAL_START_TIME_VALUE,
      endTime: INITIAL_END_TIME_VALUE,
      selectedInterviewSlots: []
    };
  },
  computed: {
    ...mapDataGetters({
      isFetchingLocations: GET_IS_FETCHING_DATA,
      locations: GET_LOCATIONS
    }),
    dialogSettings() {
      return {
        dialog: true,
        title: "Add Slots"
      };
    },
    interviewFormatId() {
      return this.interviewFormat.id;
    },
    interviewerIds() {
      return map(this.interviewers, ({ id }) => Number(id));
    },
    formattedLocations() {
      return this.locations.map(location => ({
        label: getFormattedLocation(location),
        value: location
      }));
    },

    formattedInterviewers() {
      return map(this.interviewers, interviewer => ({
        ...interviewer,
        fullName: getFullName(interviewer)
      }));
    },
    currentBookingId() {
      return this.$route.params.id;
    },
    availableSlotsParams() {
      const {
        allParticipantsAvailable,
        minimumSchedulingNotice,
        bufferBeforeSlot,
        bufferAfterSlot
      } = this.interviewSchedulingSettings;
      return {
        ...getAvailableSlotParamsForGivenMonth({
          date: this.calendarDate,
          users: this.interviewerIds,
          allParticipantsAvailable,
          bufferBeforeSlot,
          minimumSchedulingNotice,
          bufferAfterSlot
        }),
        ...(this.edit && { include_filled: 0 }),
        ...(this.startTime && { start_time: this.startTime }),
        ...(this.endTime && { end_time: this.endTime })
      };
    }
  },
  methods: {
    ...mapInterviewActions({
      fetchInterviewSlots: FETCH_INTERVIEW_FORMAT_AVAILABLE_SLOTS
    }),
    saveSlots() {
      this.$emit("saveSelectedSlots", this.selectedInterviewSlots);
    },
    onClose() {
      this.$emit("close");
    },
    updateTimeRange({ fromHour, toHour }) {
      this.startTime = fromHour;
      this.endTime = toHour;
      this.clearSelectedDays();
      this.loadAvailableSlots();
    },
    onDateChange(newDate) {
      this.calendarDate = newDate;
      this.loadAvailableSlots();
    },
    onDayClick({ date }) {
      if (!find(this.events, ["start", date])) {
        return;
      }
      if (includes(this.selectedDays, date)) {
        changeCalendarDaySelected({ date, selected: false });
        this.selectedDays = filter(this.selectedDays, day => day !== date);
      } else {
        changeCalendarDaySelected({ date, selected: true });
        this.selectedDays = [...this.selectedDays, date];
      }
    },
    updateInterviewSchedulingSettings(key, value) {
      this.interviewSchedulingSettings[key] = value;
    },
    unselectSlot(slotData) {
      if (slotData.existing) {
        this.$emit("onRemoveSlot", slotData);
      }
    },
    async handleInterviewSchedulingSettingsChange(key, val) {
      this.updateInterviewSchedulingSettings(key, val);
      if (isNumber(val) || size(val)) {
        this.clearSelectedDays();
        this.loadAvailableSlots();
      }
    },
    selectAll(selectedDay) {
      forEach(this.availableSlots[selectedDay], ({ value }) => {
        if (!find(this.selectedInterviewSlots, value)) {
          this.selectedInterviewSlots.push(value);
        }
      });
    },
    clearSelectedDays() {
      forEach(this.selectedDays, day =>
        changeCalendarDaySelected({ date: day, selected: false })
      );
      this.selectedDays = [];
      this.availableSlots = {};
    },
    async onSlotSelect(val) {
      this.selectedInterviewSlots = val;
    },
    async loadAvailableSlots() {
      if (!size(this.interviewers)) {
        return;
      }
      this.isLoadingAvailableSlots = true;
      const { data, meta } = await this.fetchInterviewSlots({
        interviewFormatId: this.interviewFormatId,
        params: this.availableSlotsParams
      });
      if (meta && data) {
        this.adjustCalendar(meta);
        this.adjustAvailableSlots(data);
      }
      this.isLoadingAvailableSlots = false;
    },
    adjustCalendar({ maxPerDay, perDay }) {
      const dates = Object.keys(perDay);
      this.events = map(dates, date => ({
        name: date,
        start: date,
        end: date,
        color: getColorBySlotAvailability(perDay[date], maxPerDay)
      }));
      delay(() => {
        removeEventsFromInactiveDayFields();
        forEach(this.selectedDays, day =>
          changeCalendarDaySelected({ date: day, selected: true })
        );
      }, 1000);
    },
    adjustAvailableSlots(newAvailableSlots) {
      const newFormattedSlots = newAvailableSlots.reduce(
        (formattedSlots, slot) => {
          const { startTime: startDate, endTime: endDate, existing } = slot;
          const formattedDate = formatDate({ date: startDate });
          if (this.availableSlots[formattedDate]) {
            return formattedSlots;
          }
          const formattedSlot = {
            label: getDateRange({ startDate, endDate, format: "HH:mm" }),
            value: slot
          };
          if (existing) {
            this.selectedInterviewSlots.push(slot);
          }
          if (formattedSlots[formattedDate]) {
            formattedSlots[formattedDate] = [
              ...formattedSlots[formattedDate],
              formattedSlot
            ];
          } else {
            formattedSlots[formattedDate] = [formattedSlot];
          }
          return formattedSlots;
        },
        {}
      );
      this.availableSlots = { ...this.availableSlots, ...newFormattedSlots };
    },
    pluralize,
    size
  },
  props: {
    interviewFormat: {
      type: Object,
      required: true
    },
    interviewers: {
      type: Array,
      required: true
    },
    externalSelectedSlots: Array
  },
  filters: {
    dayFormatLong(date) {
      return formatDate({
        date,
        startFormat: DATE_FORMAT_WITH_SEPARATOR_REVERSED,
        endFormat: LONG_DATE_FORMAT
      });
    },
    slotsCount(arr) {
      const count = size(arr);
      return `${count} ${pluralize("slots", count)}`;
    }
  }
};
</script>

<style lang="scss"></style>
